import { useCallback, useContext, useState } from 'react'
import tokens from 'config/constants/tokens'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { Flex, Button, AutoRenewIcon, useModal, Box, Skeleton } from 'vvs-uikit'
import { useTranslation } from 'contexts/Localization'
import ConnectWalletButton from 'components/ConnectWalletButton'
import StepIndicator from 'components/StepIndicator'
import { useUserVvsBar } from 'state/xVvs/hooks'
import useVvsBarHandlers from 'views/xVvs/hooks/useVvsBarHandlers'
import { useERC20 } from 'hooks/useContract'
import { useAppDispatch } from 'state'
import { fetchVvsBarUserDataAsync } from 'state/xVvs'
import xVvsContext from 'views/xVvs/context'
import { useTokenBalance } from 'state/tokens/useTokensUserBalance'
import StakeModal from '../StakeModal'

interface Props {
  apr: number
}

const ActionButtons = ({ apr }: Props) => {
  const { t } = useTranslation()
  const { account } = useActiveWeb3React()
  const { balance } = useTokenBalance(tokens.xvvs.address)

  const { onReset } = useContext(xVvsContext)

  const [openStakeModal] = useModal(
    <StakeModal customOnDismiss={onReset} isUnstaking={false} apr={apr} />,
    true,
    true,
    'stake-modal',
  )
  const [openUnstakeModal] = useModal(
    <StakeModal customOnDismiss={onReset} isUnstaking apr={apr} />,
    true,
    true,
    'unstake-modal',
  )

  const [requestedApproval, setRequestedApproval] = useState<boolean>()
  const dispatch = useAppDispatch()

  const vvsContract = useERC20(tokens.vvs.address)

  const { barData, userDataLoaded } = useUserVvsBar()
  const { allowance } = barData

  const { onApprove } = useVvsBarHandlers()

  const hasStakedVVS = balance && balance.isGreaterThan('0')
  const isEnabled = allowance && allowance.isGreaterThan('0')

  const handleApprove = useCallback(async () => {
    try {
      setRequestedApproval(true)
      await onApprove(vvsContract)
      dispatch(fetchVvsBarUserDataAsync(account))
      setRequestedApproval(false)
    } catch (e) {
      console.error(e)
    }
  }, [onApprove, dispatch, account, vvsContract])

  const handleOpenStakeModal = () => {
    onReset()
    openStakeModal()
  }
  const handleOpenUnstakeModal = () => {
    onReset()
    openUnstakeModal()
  }

  if (!account) {
    return <ConnectWalletButton scale="lg" width="100%" mb="8px" />
  }

  if (!userDataLoaded) {
    return <Skeleton width="100%" height="40px" />
  }

  if (isEnabled && hasStakedVVS) {
    return (
      <Flex flexDirection="row" justifyContent="space-between">
        <Button scale="lg" variant="primary" width="100%" mb="8px" onClick={handleOpenStakeModal}>
          {t('Stake More')}
        </Button>
        <Button scale="lg" variant="secondary" width="100%" mb="8px" ml="14px" onClick={handleOpenUnstakeModal}>
          {t('Unstake')}
        </Button>
      </Flex>
    )
  }

  return (
    <Box>
      <Flex flexDirection="row" justifyContent="space-between">
        <Button
          scale="lg"
          variant="primary"
          width="100%"
          mb="8px"
          onClick={handleApprove}
          isLoading={requestedApproval}
          endIcon={requestedApproval ? <AutoRenewIcon spin color="currentColor" /> : null}
          disabled={isEnabled}
        >
          {isEnabled ? t('Enabled') : t('Enable')}
        </Button>
        <Button
          scale="lg"
          variant="primary"
          width="100%"
          mb="8px"
          ml="14px"
          onClick={openStakeModal}
          disabled={!isEnabled}
        >
          {t('Stake VVS')}
        </Button>
      </Flex>
      <StepIndicator steps={2} currentStep={isEnabled ? 2 : 1} mt={3} />
    </Box>
  )
}

export default ActionButtons
